<template>
    <kimsa-grid-table :headers="headers" :items="items" :loading="loading" item-key="key" sort-by="name"
        :per-page="perPage" :page="page" :skeleton-count="6" :no-data-text="noDataText">
        <template v-slot:item.name="{ item }">
            <button-tooltip top :tooltip="$t('business.see_payments')">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="client-main" v-bind="attrs" v-on="on" @click="$emit('history', item)">
                        <div class="client-avatar" :style="item.avatarGradient">{{ item.initials }}</div>
                        <div class="client-identity">
                            <truncated-text class="client-name" :text="item.name" />
                            <truncated-text class="client-email" :text="item.email || $t('business.no_email')" />
                        </div>
                    </button>
                </template>
            </button-tooltip>
        </template>

        <template v-slot:item.source="{ item }">
            <span class="source-chip" :class="`source-chip--${item.source}`">{{ item.sourceLabel }}</span>
        </template>

        <template v-slot:item.nextPayment="{ item }">
            <span :class="{ 'text-alert': item.status === 'overdue' }">{{ item.nextPaymentLabel }}</span>
        </template>

        <template v-slot:item.planAmount="{ item }">
            <span class="client-amount">{{ item.planLabel }}</span>
        </template>

        <template v-slot:item.method="{ item }">
            <span class="client-muted">{{ item.methodLabel }}</span>
        </template>

        <template v-slot:item.status="{ item }">
            <span class="status-badge" :class="`status-badge--${item.status}`">{{ item.statusLabel }}</span>
        </template>

        <template v-slot:item.months="{ item }">
            <span class="client-muted">
                {{ item.months === null ? $t('business.no_value') : $tc('business.months_count', item.months, { n: item.months }) }}
            </span>
        </template>

        <template v-slot:item.active="{ item }">
            <kimsa-toggle :value="item.active" :loading="item.id === togglingId"
                @change="$emit('toggle:active', item)" />
        </template>

        <template v-slot:item.actions="{ item }">
            <div class="row-actions">
                <button-tooltip top :tooltip="$t('business.register_payment')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="icon-button" v-bind="attrs" v-on="on"
                            @click="$emit('charge', item)">
                            <v-icon size="15">mdi-cash-plus</v-icon>
                        </button>
                    </template>
                </button-tooltip>

                <button-tooltip top :tooltip="$t('business.client_file')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="icon-button" v-bind="attrs" v-on="on" @click="$emit('edit', item)">
                            <v-icon size="15">mdi-account-details-outline</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>
        </template>

        <template v-slot:footer>
            <slot name="footer" />
        </template>
    </kimsa-grid-table>
</template>

<script>
import KimsaGridTable from "@/components/common/KimsaGridTable";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import TruncatedText from "@/components/common/TruncatedText";
import ButtonTooltip from "@/components/common/ButtonTooltip";

export default {
    name: "ClientsTable",
    components: { KimsaGridTable, KimsaToggle, TruncatedText, ButtonTooltip },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        perPage: {
            type: Number,
            default: 15,
        },
        page: {
            type: Number,
            default: 1,
        },
        togglingId: {
            type: String,
            default: null,
        },
        noDataText: {
            type: String,
            default: '',
        },
    },
    computed: {
        headers(vm = this) {
            void vm.$i18n.locale
            return [
                { text: vm.$t('business.client'), value: 'name' },
                { text: vm.$t('business.source'), value: 'source', width: '110px' },
                { text: vm.$t('business.next_payment'), value: 'nextPayment', width: '130px' },
                { text: vm.$t('business.plan_per_month'), value: 'planAmount', width: '130px' },
                { text: vm.$t('business.method'), value: 'method', width: '130px', sortable: false },
                { text: vm.$t('business.status'), value: 'status', width: '130px', sortable: false },
                { text: vm.$t('business.seniority'), value: 'months', width: '120px' },
                { text: vm.$t('common.active'), value: 'active', width: '90px', sortable: false },
                { text: '', value: 'actions', width: '96px', align: 'center', sortable: false, stickyEnd: true },
            ]
        },
    },
}
</script>

<style scoped>
.client-main {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    width: 100%;
    text-align: left;
}

.client-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
}

.client-identity {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.client-name {
    font-size: var(--fs-label);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.client-email {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.client-amount {
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.client-muted {
    color: var(--v-txtMuted-base);
}

.text-alert {
    color: var(--v-error-base);
}

.status-badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.status-badge--ok {
    background: rgba(102, 204, 153, 0.16);
    color: #66CC99;
}

.status-badge--due {
    background: rgba(230, 172, 0, 0.16);
    color: #E6AC00;
}

.status-badge--overdue {
    background: rgba(232, 92, 92, 0.16);
    color: var(--v-error-base);
}

.status-badge--none {
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
}

.source-chip {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 8px;
    border-radius: 20px;
    border: 1px solid var(--v-outline-base);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.source-chip--app {
    border-color: rgba(102, 204, 204, 0.4);
    color: #66CCCC;
}

.icon-button {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    transition: color 0.15s, border-color 0.15s;
}

.icon-button:hover {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-outline2-base);
}

.row-actions {
    display: flex;
    justify-content: center;
    gap: 6px;
    opacity: 0;
    transition: opacity 0.15s;
}

>>> .kgt-row:hover .row-actions {
    opacity: 1;
}

@media (min-width: 961px) {
    >>> .kgt-row:hover .kgt-sticky {
        background: linear-gradient(to right, transparent, var(--v-surface2-base) 28px);
    }
}

@media (max-width: 960px) {
    .row-actions {
        opacity: 1;
    }
}
</style>
